import { Component, Input } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { RootState, selectActualReconDate } from '@helios/state-management'
import { IntegerIdValidator } from '@helios/validators'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { DisplayDateFormat, toISOFormat } from '@northfork/basics'
import { take } from 'rxjs/operators'
import { Break } from '../../model/break.model'
import {
  AccountUser,
  CreateIssueRequest,
  IssueDescriptionRequest,
  IssueIdName,
} from '../../model/issue.model'
import { ReconInsightService } from '../../services/recon-insight.service'

@Component({
  selector: 'hs-create-issue-modal',
  templateUrl: './create-issue-modal.component.html',
})
export class CreateIssueModalComponent {
  private _breaks: Break[] = []

  dateFormat = DisplayDateFormat
  breakIds: string[] = []
  modalTitle = 'Create Issue'
  saveBtnLabel = 'Save'
  issueName = new FormControl(null, [Validators.required])
  securityId = new FormControl(null, [Validators.required])
  ticker = new FormControl(null, [])
  clearwaterValue = new FormControl(null, [])
  clientValue = new FormControl(null, [])
  beginDate = new FormControl(null, [Validators.required])
  issueType = new FormControl(null, [Validators.required])
  issueSource = new FormControl(null, [Validators.required])
  priority = new FormControl(null, [Validators.required])
  assignee = new FormControl(null, [Validators.required])
  description = new FormControl(null, [])
  accountId = new FormControl(null, [IntegerIdValidator])

  selectedFiles: FormControl = new FormControl([] as File[])

  //fill options on init
  loadingOptions = false
  issueTypeName? = ''
  issueTypeOptions: IssueIdName[] = []
  issueSourceOptions: IssueIdName[] = []
  priorityOptions: IssueIdName[] = []
  assigneeOptions: AccountUser[] = []
  issueDescription? = ''
  metaData?: Record<string, unknown>
  issueTypeNameCode? = ''
  issueStoreIssueId? = ''

  @Input() set breaks(values: Break[]) {
    if (values.length === 0) {
      this.activeModal.dismiss()
    }

    this._breaks = values
    this.breakIds = values.map(b => b.identifier)
    if (values.length > 1) {
      this.modalTitle = `Create Issues (${values.length})`
      this.saveBtnLabel = `Save (${values.length})`
    }
  }

  get breaks(): Break[] {
    return this._breaks
  }

  formSubmitting = false
  form = new FormGroup({
    issueName: this.issueName,
    securityId: this.securityId,
    beginDate: this.beginDate,
    issueType: this.issueType,
    issueSource: this.issueSource,
    priority: this.priority,
    assignee: this.assignee,
  })

  constructor(
    public activeModal: NgbActiveModal,
    private readonly reconInsightService: ReconInsightService,
    private readonly store: Store<RootState>,
  ) {}

  ngOnInit() {
    this.loadingOptions = true
    let selectedBreak
    if (this._breaks.length > 0) {
      selectedBreak = this._breaks.pop()
      this.accountId.setValue(selectedBreak?.acctId)
      this.securityId.setValue(selectedBreak?.securityId)
      this.issueTypeName = selectedBreak?.displayName
      this.issueName.setValue(selectedBreak?.displayName)
      this.metaData = selectedBreak?.metaData
      this.issueTypeNameCode = selectedBreak?.typeName
      this.issueStoreIssueId = selectedBreak?.identifier
    }

    this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .subscribe(date => {
        this.beginDate.setValue(toISOFormat(date))
      })

    this.initOptions().then(() => (this.loadingOptions = false))
  }

  async onSubmit() {
    this.formSubmitting = true

    const createIssueRequest: CreateIssueRequest = {
      name: this.issueName.value,
      accountId: this.accountId.value,
      securityIdentifier: this.securityId.value,
      ticker: this.ticker.value,
      beginDate: this.beginDate.value,
      clearwaterValue: this.clearwaterValue.value,
      clientValue: this.clientValue.value,
      issueTypeId:
        this.issueType.value == null ? null : this.issueType.value.id,
      issueSourceId: this.issueSource.value.id,
      priorityId: this.priority.value.id,
      assigneeId: this.assignee.value.id,
      description: this.description.value,
      statusId: 1,
      issueStoreIssueId: this.issueStoreIssueId,
    }

    const issueId = await this.reconInsightService
      .createReconInsightIssue(createIssueRequest)
      .toPromise()

    for (const attachment of <File[]>this.selectedFiles.value) {
      await this.reconInsightService
        .addAttachment(issueId, attachment.name, attachment)
        .toPromise()
    }
    this.selectedFiles.setValue([])
    this.formSubmitting = false
    this.activeModal.close()
  }

  /**
   * Just close the modal and reset the form
   */
  onCancel() {
    this.activeModal.dismiss()
  }

  async initOptions() {
    const issueTypes = await this.reconInsightService
      .getReconInsightIssueTypes()
      .toPromise()
    this.issueSourceOptions = await this.reconInsightService
      .getReconInsightIssueSources()
      .toPromise()
    this.priorityOptions = await this.reconInsightService
      .getReconInsightIssuePriorities()
      .toPromise()
    this.assigneeOptions = await this.reconInsightService
      .getReconInsightAccountUsers(this.accountId.value)
      .toPromise()

    if (this.metaData && this.issueTypeNameCode) {
      const issueDescriptionRequest: IssueDescriptionRequest = {
        accountId: this.accountId.value,
        typeName: this.issueTypeNameCode,
        metaData: this.metaData,
      }
      this.reconInsightService
        .getReconInsightIssueDescription(issueDescriptionRequest)
        .subscribe(data => {
          this.description.setValue(data.description)
        })
    }

    //sort issue types by name
    this.issueTypeOptions = issueTypes.sort(
      (a: IssueIdName, b: IssueIdName) => {
        if (a.name > b.name) {
          return 1
        } else if (a.name < b.name) {
          return -1
        }
        return 0
      },
    )

    if (this.issueTypeName && this.issueTypeName.length > 0) {
      this.issueTypeOptions.forEach(issueType => {
        if (issueType.name.toLowerCase() == this.issueTypeName?.toLowerCase()) {
          this.issueType.setValue(issueType)
        }
      })
    }

    // set default values
    this.issueSource.setValue(
      this.issueSourceOptions == null || this.issueSourceOptions.length == 0
        ? null
        : this.issueSourceOptions[0],
    )
    this.priority.setValue(
      this.priorityOptions == null || this.priorityOptions.length == 0
        ? null
        : this.priorityOptions[0],
    )
  }
}
